// src/App.tsx
import React, { useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Outlet,
} from 'react-router-dom';

import { useAuth } from './hooks/useAuth';
import { createCrudRoutes } from './router/routeWrappers';
import { showDevTools } from './config/env';

import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import LoginScreen from './Layouts/Authentication/LoginScreen';
import LogoutModal from './Layouts/Authentication/LogoutModal';

// ── Page import ──────────────────────────────────────────────────────────────
import Dashboard                    from './Layouts/Dashboard/Dashboard';
import StudentsListDashboard        from './Layouts/Students/StudentsListDashboard';
import TeachersList                 from './Layouts/Teachers/TeachersList';
import Noticeboard                  from './Layouts/Noticeboard/Noticeboard';
import StudentIssues                from './Layouts/StudentIssues/StudentIssues';
import StudentCredentialsGenerator  from './Layouts/StudentCredentialsGenerator/StudentCredentialsGenerator';
import DateSheet                    from './Layouts/Exams/DateSheet';
import Timetable                    from './Layouts/Timetable/Timetable';
import BulkPasswordReset            from './Layouts/BulkPasswordReset/BulkPasswordReset';
import Settings                     from './Layouts/Settings/Settings';
import StudentPDFDownloader         from './Layouts/StudentsPdfGenerator/StudentPDFDownloader';
import HallTicketGenerator          from './Layouts/HallTicketGenerator/HallTicketGenerator';
import FeesPanel                    from './Layouts/FeesPanel/FeesPanel';
import Events                       from './Layouts/Events/Events';
import SchoolLetterHeader           from './Layouts/LetterHeaderGenerator/SchoolLetterHeader';
import SchoolLetterTemplate         from './Layouts/LetterHeaderGenerator/SchoolLetterTemplate';
import LetterheadListing            from './Layouts/LetterHeaderGenerator/LetterheadListing';
import LetterheadTemplateListing    from './Layouts/LetterHeaderGenerator/LetterheadTemplateListing';
import AssignedTeachersDashboard    from './Layouts/Timetable/sub-components/AssignedTeachersDashboard';
import BulkProfilePictureUpload     from './Layouts/Students/BulkProfilePictureUpload';
import StaffList                    from './Layouts/Staff/StaffList';
import UDISEAnomalyDetector         from './Layouts/StudentsPdfGenerator/UDISEAnomalyDetector';
import StaffAttendance              from './Layouts/StaffAttendance/StaffAttendance';
import GatePassGenerator            from './Layouts/HallTicketGenerator/GatePassGenerator';
import PayrollManagement            from './Layouts/PayrollManagement/PayrollManagement';
import TeacherCredentialsGenerator  from './Layouts/StudentCredentialsGenerator/TeacherCredentialsGenerator';
import AwardRole                    from './Layouts/AwardRole/AwardRole';
import PayrollRecords               from './Layouts/PayrollManagement/PayrollRecords';
import ModernTimetablePage          from './Layouts/Timetable/ModernTimetablePage';
import SubjectsDashboard            from './Layouts/SubjectsSetup/SubjectsDashboard';
import ClassAttendanceOverview      from './Layouts/StudentAttendance/ClassAttendanceOverview';
import FeeHistoryPanel              from './Layouts/FeesPanel/FeeHistoryPanel';
import VisitorLogDashboard          from './Layouts/VisitorLogs/VisitorLogDashboard';
import ExpenseDashboard             from './Layouts/Expenses/ExpenseDashboard';
import GatePassDashboard            from './Layouts/GatePasses/GatePassDashboard';
import GateEntryDashboard           from './Layouts/GatePasses/GateEntryDashboard';
import { ruleOf }                   from './stateManagers/roleAccess/access';
import { Guard }                    from './stateManagers/roleAccess/useRoleAccess';

// ── CRUD route pairs ──────────────────────────────────────────────────────────
const Letterhead     = createCrudRoutes(SchoolLetterHeader,   '/letterheads');
const LetterTemplate = createCrudRoutes(SchoolLetterTemplate, '/letterhead-templates');

// ── One guard for every page ──────────────────────────────────────────────────
// Looks up the current URL in ROUTE_ACCESS (access.ts). No per-route Guards needed.
const RouteGuard: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <Guard rule={ruleOf(pathname)}>
      <Outlet />
    </Guard>
  );
};

// ── Inner app (needs to be inside BrowserRouter for hooks) ────────────────────
const AppInner: React.FC = () => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  if (loading)          return <LoadingScreen />;
  if (!isAuthenticated) return <LoginScreen />;

  return (
    <>
      <Routes>
        {/* Pathless layout route — MainLayout wraps every child via <Outlet />. */}
        <Route
          element={
            <MainLayout
              user={user}
              onNavigate={navigate}
              onLogout={() => setShowLogoutModal(true)}
            />
          }
        >
          {/* Pathless guard route — protects everything below using access.ts */}
          <Route element={<RouteGuard />}>

            {/* ── Dashboard ── */}
            <Route index element={<Dashboard />} />

            {/* ── Students ── */}
            <Route path="students">
              <Route index                element={<StudentsListDashboard />} />
              <Route path="bulk-photos"   element={<BulkProfilePictureUpload />} />
              <Route path="attendance"    element={<ClassAttendanceOverview />} />
            </Route>

            {/* ── Teachers ── */}
            <Route path="teachers" element={<TeachersList />} />

            {/* ── Staff ── */}
            <Route path="staff">
              <Route index                    element={<StaffList />} />
              <Route path="staff-attendance"  element={<StaffAttendance />} />
            </Route>

            {/* ── Payroll ── */}
            <Route path="payroll-management">
              <Route index          element={<PayrollManagement />} />
              <Route path="records" element={<PayrollRecords />} />
            </Route>

            {/* ── Noticeboard / Events ── */}
            <Route path="noticeboard" element={<Noticeboard />} />
            <Route path="events"      element={<Events />} />

            {/* ── Student Issues ── */}
            <Route path="student-issues" element={<StudentIssues />} />

            {/* ── Credentials ── */}
            <Route path="credentials">
              <Route path="students" element={<StudentCredentialsGenerator />} />
              <Route path="teachers" element={<TeacherCredentialsGenerator />} />
            </Route>

            {/* ── Misc flat screens ── */}
            <Route path="bulk-password-reset" element={<BulkPasswordReset />} />

            {/* ── Exams ── */}
            <Route path="exams">
              <Route index             element={<DateSheet />} />
              <Route path="award-role" element={<AwardRole />} />
            </Route>

            {/* ── Visitor Log ── */}
            <Route path="visitors" element={<VisitorLogDashboard />} />

            {/* ── Gate ── */}
            <Route path="gate-passes" element={<GatePassDashboard />} />
            <Route path="gate-entry"  element={<GateEntryDashboard />} />

            {/* ── Expenses ── */}
            <Route path="expenses" element={<ExpenseDashboard />} />

            {/* ── Record Reports ── */}
            <Route path="students-pdf">
              <Route index                       element={<StudentPDFDownloader />} />
              <Route path="UDISEAnomalyDetector" element={<UDISEAnomalyDetector />} />
            </Route>

            {/* ── Hall Tickets ── */}
            <Route path="hall-tickets">
              <Route index            element={<HallTicketGenerator />} />
              <Route path="gate-pass" element={<GatePassGenerator />} />
            </Route>

            {/* ── Fees ── */}
            <Route path="fees">
              <Route index          element={<FeesPanel />} />
              <Route path="history" element={<FeeHistoryPanel />} />
            </Route>

            {/* ── Settings ── */}
            <Route path="settings" element={<Settings />} />

            {/* ── Subjects — local dev / beta only (admin-only in access.ts) ── */}
            {showDevTools && (
              <Route path="subjects" element={<SubjectsDashboard />} />
            )}

            {/* ── Timetable ── */}
            <Route path="timetable">
              <Route index                     element={<Timetable />} />
              <Route path="modern"             element={<ModernTimetablePage />} />
              <Route path="assigned-teachers"  element={<AssignedTeachersDashboard />} />
            </Route>

            {/* ── Letterheads (list + CRUD) ── */}
            <Route path="letterheads">
              <Route index           element={<LetterheadListing />} />
              <Route path="new"      element={<Letterhead.New />} />
              <Route path="edit/:id" element={<Letterhead.Edit />} />
            </Route>

            {/* ── Letter Templates (list + CRUD) ── */}
            <Route path="letterhead-templates">
              <Route index           element={<LetterheadTemplateListing />} />
              <Route path="new"      element={<LetterTemplate.New />} />
              <Route path="edit/:id" element={<LetterTemplate.Edit />} />
            </Route>

            {/*
             * Adding a new screen:
             *   1. Add the <Route> here (no Guard needed).
             *   2. Add ONE line to ROUTE_ACCESS in access.ts.
             *   3. Add it to menuConfig in Sidebar.tsx (if it needs a menu item).
             *
             * CRUD example:
             *   const Widget = createCrudRoutes(WidgetForm, '/widgets');
             *
             *   <Route path="widgets">
             *     <Route index           element={<WidgetList />} />
             *     <Route path="new"      element={<Widget.New />} />
             *     <Route path="edit/:id" element={<Widget.Edit />} />
             *   </Route>
             */}

            {/* ── Catch-all ── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Routes>

      <LogoutModal
        visible={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        onConfirm={async () => { await logout(); setShowLogoutModal(false); }}
      />
    </>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────
const App: React.FC = () => (
  <BrowserRouter>
    <AppInner />
  </BrowserRouter>
);

export default App;